/**
 * Telas de editais: filtros na query string da rota, consultas à API e formatação.
 * O validador dos filtros é escrito à mão (sem zod no bundle) e tolera o que vier da URL.
 */
import type { EditaisResponse, EditalDetalhe, EditalTrechos, SincronizacaoResponse } from '@licita/contracts';
import { UFS } from '@licita/contracts';
import { queryOptions } from '@tanstack/react-query';
import { api } from './api';

export const ORDENS = {
  publicacao: 'Publicação',
  encerramento: 'Encerramento',
  valor: 'Valor',
  relevancia: 'Relevância',
} as const;
export type Ordem = keyof typeof ORDENS;

/** Filtros da lista, como ficam na URL (`/editais?q=...&uf=SP`). Só o que difere do padrão é gravado. */
export interface Filtros {
  q?: string;
  modalidade?: number;
  uf?: (typeof UFS)[number];
  ti: boolean;
  comTexto: boolean;
  abertos: boolean;
  publicadoDe?: string;
  publicadoAte?: string;
  ordem: Ordem;
  pagina: number;
  porPagina: number;
}

export const FILTROS_PADRAO: Filtros = { ti: true, comTexto: false, abertos: false, ordem: 'publicacao', pagina: 1, porPagina: 25 };

const DATA = /^\d{4}-\d{2}-\d{2}$/;
const flag = (v: unknown, padrao: boolean) => (v === undefined || v === null ? padrao : v === true || v === 'true');
const inteiro = (v: unknown, min: number, max: number, padrao: number) => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isInteger(n) && n >= min && n <= max ? n : padrao;
};
const texto = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim().slice(0, 200) : undefined);

export function validarFiltros(raw: Record<string, unknown>): Filtros {
  const modalidade = inteiro(raw.modalidade, 1, 13, 0);
  const uf = typeof raw.uf === 'string' && (UFS as readonly string[]).includes(raw.uf) ? (raw.uf as Filtros['uf']) : undefined;
  const ordem = typeof raw.ordem === 'string' && raw.ordem in ORDENS ? (raw.ordem as Ordem) : 'publicacao';
  const dataOk = (v: unknown) => (typeof v === 'string' && DATA.test(v) ? v : undefined);
  return {
    q: texto(raw.q),
    modalidade: modalidade || undefined,
    uf,
    ti: flag(raw.ti, true),
    comTexto: flag(raw.comTexto, false),
    abertos: flag(raw.abertos, false),
    publicadoDe: dataOk(raw.publicadoDe),
    publicadoAte: dataOk(raw.publicadoAte),
    ordem,
    pagina: inteiro(raw.pagina, 1, 1_000_000, 1),
    porPagina: inteiro(raw.porPagina, 10, 100, 25),
  };
}

/** Query string para a API (booleanos como 'true'/'false', como o contrato espera). */
export function filtrosParaQuery(f: Filtros): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== '') p.set(k, String(v));
  return p.toString();
}

export const editaisQuery = (f: Filtros) =>
  queryOptions({
    queryKey: ['editais', f],
    queryFn: () => api<EditaisResponse>(`/admin/editais?${filtrosParaQuery(f)}`),
    placeholderData: (prev) => prev,
  });

export const editalQuery = (id: string) =>
  queryOptions({ queryKey: ['edital', id], queryFn: () => api<EditalDetalhe>(`/admin/editais/${id}`) });

export const trechosQuery = (id: string, q: string) =>
  queryOptions({
    queryKey: ['edital', id, 'trechos', q],
    queryFn: () => api<EditalTrechos>(`/admin/editais/${id}/trechos?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 2,
  });

export const sincronizacaoQuery = queryOptions({
  queryKey: ['sincronizacao'],
  queryFn: () => api<SincronizacaoResponse>('/admin/sincronizacao'),
});

export const linkDocumento = (editalId: string, docId: string) =>
  api<{ url: string }>(`/admin/editais/${editalId}/documentos/${docId}/link`);

// ---------- formatação ----------

/** Centavos → "R$ 1.234" (sem centavos, para a lista densa). "—" se nulo. */
export const moneyInteiro = (minor: number | null | undefined) =>
  minor == null
    ? '—'
    : (minor / 100)
        .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 })
        .replace(/\u00a0/g, ' ');

export const inteiroBR = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('pt-BR'));

const fmtData = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeZone: 'America/Sao_Paulo' });
const fmtDataHora = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' });
/** Instante ISO → "20/10/2026" no fuso de Brasília. */
export const dataBR = (iso: string | null | undefined) => (iso ? fmtData.format(new Date(iso)) : '—');
export const dataHoraBR = (iso: string | null | undefined) => (iso ? fmtDataHora.format(new Date(iso)) : '—');

/** Prazo relativo ao agora: "encerrado", "hoje", "em 3 dias". */
export function prazo(iso: string | null | undefined, agora = Date.now()): { texto: string; tone: 'muted' | 'warn' | 'bad' | 'ok' } {
  if (!iso) return { texto: '—', tone: 'muted' };
  const ms = new Date(iso).getTime() - agora;
  if (ms < 0) return { texto: 'encerrado', tone: 'muted' };
  const dias = Math.ceil(ms / 86_400_000);
  if (dias <= 0) return { texto: 'hoje', tone: 'bad' };
  if (dias === 1) return { texto: 'em 1 dia', tone: 'bad' };
  return { texto: `em ${dias} dias`, tone: dias <= 7 ? 'warn' : 'ok' };
}

export const bytes = (n: number | null | undefined) => {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
};

/** `CNPJ-1-SEQ/ANO` → https://pncp.gov.br/app/editais/{cnpj}/{ano}/{sequencial} (sem zeros à esquerda). */
export function pncpUrl(numeroControle: string): string | null {
  const m = /^(\d+)-\d+-(\d+)\/(\d{4})$/.exec(numeroControle.trim());
  if (!m) return null;
  const semZeros = (s: string) => String(Number(s));
  return `https://pncp.gov.br/app/editais/${semZeros(m[1]!)}/${m[3]}/${semZeros(m[2]!)}`;
}

export const STATUS_DOC: Record<string, { rotulo: string; tone: 'ok' | 'warn' | 'bad' | 'info' | 'muted' }> = {
  pendente: { rotulo: 'pendente', tone: 'muted' },
  baixado: { rotulo: 'baixado', tone: 'info' },
  extraido: { rotulo: 'extraído', tone: 'ok' },
  sem_texto: { rotulo: 'sem texto', tone: 'warn' },
  ignorado: { rotulo: 'ignorado', tone: 'muted' },
  erro: { rotulo: 'erro', tone: 'bad' },
};
